import React, { useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import SearchIcon from "@mui/icons-material/Search";
import { useForm, useFormState } from "react-final-form";
import { TextField } from "./TextField";
import { AddressSearchDialog } from "@/shared/components";
import type { DaDataAddressSuggestionType } from "@/shared/types/common";

/**
 * Компонент формы для ввода адреса
 *
 * Автоматически создает все поля адреса:
 * - zipCode - почтовый индекс
 * - country - страна
 * - region - регион
 * - city - город
 * - settlement - населенный пункт
 * - street - улица
 * - building - дом
 * - apartment - квартира
 * - flat - помещение
 * - extra - дополнительная информация
 * - fiasId - ФИАС ID
 * - lat - широта
 * - lon - долгота
 *
 * @example
 * // Использование в схеме FormBuilder
 * const schema = {
 *   address: {
 *     type: "address",
 *     label: "Адрес организации",
 *     xs: 12,
 *     fullWidth: true,
 *   }
 * }
 *
 * // Результат в values формы:
 * // {
 * //   address: {
 * //     zipCode: "123456",
 * //     country: "Россия",
 * //     region: "Московская область",
 * //     city: "Москва",
 * //     settlement: "село Корткерос",
 * //     street: "ул. Ленина",
 * //     building: "10",
 * //     apartment: "5",
 * //     flat: "офис 15",
 * //     extra: "дополнительная информация",
 * //     fiasId: "uuid",
 * //     lat: 55.755831,
 * //     lon: 37.617673
 * //   }
 * // }
 */

interface AddressFieldProps {
  name: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  metadata?: Record<string, any>;
}

export const AddressField: React.FC<AddressFieldProps> = ({
  name,
  label,
  disabled,
  required,
  helperText,
  fullWidth = true,
  metadata,
}) => {
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const form = useForm();
  const formState = useFormState();

  // Получаем regionId из metadata (если есть) или из формы (fallback)
  const regionId = metadata?.regionId || formState.values?.regionId;

  const handleOpenSearchDialog = useCallback(() => {
    setIsSearchDialogOpen(true);
  }, []);

  const handleCloseSearchDialog = useCallback(() => {
    setIsSearchDialogOpen(false);
  }, []);

  const handleSelectAddress = useCallback(
    (suggestion: DaDataAddressSuggestionType) => {
      const { data } = suggestion;

      // Маппинг полей DaData в наш формат Address
      const addressFields = {
        [`${name}.zipCode`]: data.postal_code || "",
        [`${name}.country`]: data.country || "",
        [`${name}.region`]: data.region || "",
        [`${name}.city`]: data.city || "",
        [`${name}.settlement`]: data.settlement || "",
        [`${name}.street`]: data.street || "",
        [`${name}.building`]: data.house || "",
        [`${name}.apartment`]: "",
        [`${name}.flat`]: data.flat || "",
        [`${name}.extra`]: "",
        [`${name}.fiasId`]: data.fias_id || "",
        [`${name}.lat`]: Number(data.geo_lat) || undefined,
        [`${name}.lon`]: Number(data.geo_lon) || undefined,
      };
      // Обновляем все поля адреса в форме
      Object.entries(addressFields).forEach(([fieldName, value]) => {
        form.change(fieldName, value);
      });
    },
    [name, form]
  );

  return (
    <div>
      {label && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 1,
          }}
        >
          <Typography variant="h6">
            {label}
            {required && " *"}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SearchIcon />}
            onClick={handleOpenSearchDialog}
            disabled={disabled}
          >
            Найти адрес
          </Button>
        </Box>
      )}
      {helperText && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {helperText}
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid size={4}>
          <TextField
            name={`${name}.zipCode`}
            label="Почтовый индекс"
            disabled={disabled}
            fullWidth={fullWidth}
            required={required}
          />
        </Grid>

        <Grid size={4}>
          <TextField
            name={`${name}.country`}
            label="Страна"
            disabled={disabled}
            fullWidth={fullWidth}
            required={required}
          />
        </Grid>

        <Grid size={4}>
          <TextField
            name={`${name}.region`}
            label="Регион"
            disabled={disabled}
            fullWidth={fullWidth}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            name={`${name}.city`}
            label="Город"
            disabled={disabled}
            fullWidth={fullWidth}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            name={`${name}.settlement`}
            label="Населенный пункт"
            disabled={disabled}
            fullWidth={fullWidth}
          />
        </Grid>

        <Grid size={12}>
          <TextField
            name={`${name}.street`}
            label="Улица"
            disabled={disabled}
            fullWidth={fullWidth}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            name={`${name}.building`}
            label="Дом"
            disabled={disabled}
            fullWidth={fullWidth}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            name={`${name}.apartment`}
            label="Квартира"
            disabled={disabled}
            fullWidth={fullWidth}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            name={`${name}.flat`}
            label="Помещение"
            disabled={disabled}
            fullWidth={fullWidth}
          />
        </Grid>

        <Grid size={12}>
          <TextField
            name={`${name}.extra`}
            label="Дополнительная информация"
            disabled={disabled}
            fullWidth={fullWidth}
            multiline
            rows={3}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            name={`${name}.fiasId`}
            label="ФИАС ID"
            disabled={disabled}
            fullWidth={fullWidth}
          />
        </Grid>

        <Grid size={3}>
          <TextField
            name={`${name}.lat`}
            label="Широта"
            type="number"
            disabled={disabled}
            fullWidth={fullWidth}
          />
        </Grid>

        <Grid size={3}>
          <TextField
            name={`${name}.lon`}
            label="Долгота"
            type="number"
            disabled={disabled}
            fullWidth={fullWidth}
          />
        </Grid>
      </Grid>

      <AddressSearchDialog
        open={isSearchDialogOpen}
        onClose={handleCloseSearchDialog}
        onSelect={handleSelectAddress}
        title="Поиск адреса"
        regionId={regionId}
      />
    </div>
  );
};
